import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { orderTypeService } from '@services/orderTypeService';
import { OrderTypeFormData } from '@/types/orderType';

export const OrderTypeFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<OrderTypeFormData>({
    order_type: '',
    generate_invoice: true,
    has_financial_value: true,
  });

  useEffect(() => {
    if (isEditing && id) {
      loadData(parseInt(id));
    }
  }, [id, isEditing]);

  const loadData = async (itemId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await orderTypeService.getById(itemId);
      setFormData({
        order_type: data.order_type,
        generate_invoice: data.generate_invoice,
        has_financial_value: data.has_financial_value,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field: keyof OrderTypeFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.order_type.trim()) {
      newErrors.order_type = 'Nome do tipo é obrigatório';
    } else if (formData.order_type.length > 20) {
      newErrors.order_type = 'Nome deve ter no máximo 20 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError(null);

      if (isEditing && id) {
        await orderTypeService.update(parseInt(id), formData);
      } else {
        await orderTypeService.create(formData);
      }

      navigate('/settings/sales/order-types');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/settings/sales/order-types')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
            <div className="h-4 bg-slate-200 rounded w-32 mt-2 animate-pulse" />
          </div>
        </div>
        <Card>
          <CardContent>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-14 bg-slate-200 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/settings/sales/order-types')}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-h1">{isEditing ? 'Editar Tipo de Pedido' : 'Novo Tipo de Pedido'}</h1>
          <p className="text-slate-600">
            {isEditing ? 'Altere os dados do tipo de pedido' : 'Preencha os dados para criar um novo tipo de pedido'}
          </p>
        </div>
      </div>

      {/* Erro */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Formulário */}
      <Card>
        <CardHeader title="Dados do Tipo de Pedido" />
        <CardContent>
          <div className="space-y-6">
            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Nome do Tipo <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.order_type}
                onChange={(e) => updateField('order_type', e.target.value)}
                placeholder="Ex: Venda, Demonstração, Presente"
                maxLength={20}
              />
              {errors.order_type && (
                <p className="text-sm text-red-500 mt-1">{errors.order_type}</p>
              )}
              <p className="text-xs text-slate-500 mt-1">{formData.order_type.length}/20 caracteres</p>
            </div>

            {/* Gera Nota Fiscal */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="generate_invoice"
                checked={formData.generate_invoice}
                onChange={(e) => updateField('generate_invoice', e.target.checked)}
                className="h-4 w-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
              />
              <label htmlFor="generate_invoice" className="text-sm font-medium text-slate-700">
                Gera Nota Fiscal
              </label>
            </div>

            {/* Tem Valor Financeiro */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="has_financial_value"
                checked={formData.has_financial_value}
                onChange={(e) => updateField('has_financial_value', e.target.checked)}
                className="h-4 w-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
              />
              <label htmlFor="has_financial_value" className="text-sm font-medium text-slate-700">
                Tem Valor Financeiro
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ações */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/settings/sales/order-types')}>
          Cancelar
        </Button>
        <Button onClick={handleSave} loading={saving} icon={<Save size={18} />}>
          {isEditing ? 'Salvar Alterações' : 'Criar Tipo de Pedido'}
        </Button>
      </div>
    </div>
  );
};
